import { z } from 'zod';
import { MODULE_ACCENTS, type ModuleAccent } from './accents';
import type { ModuleManifest } from './manifest';

/**
 * Catalogs: lists a company maintains itself, whose entries carry properties, e.g. finding
 * categories with their group or the defect catalog with text templates
 * (docs/architecture/catalogs.md, ADR-0039). Modules declare catalogs in their manifest. The kernel
 * brings the statuses and priorities of findings and, for every module with findings, the groups,
 * categories and templates of its findings. Types, limits and the check of entry properties live
 * here, so the server checks what the forms check.
 */

export const CATALOG_FIELD_TYPES = ['text', 'textarea', 'number', 'boolean', 'catalog'] as const;
export type CatalogFieldType = (typeof CATALOG_FIELD_TYPES)[number];

/** A property of the entries next to the standard fields. */
export interface CatalogFieldDeclaration {
  /** Key in `data`, e.g. `dueDays`. */
  key: string;
  /** i18n key of the field name. */
  label: string;
  type: CatalogFieldType;
  /** For `catalog`: key of a catalog of the same module or of the kernel; the value is an entry ID. */
  catalog?: string;
  required?: boolean;
}

/** A catalog in the manifest of a module. */
export interface CatalogDeclaration {
  /** `<module>.<name>`, e.g. `sigeko.trades`. */
  key: string;
  /** i18n key. */
  label: string;
  /** i18n key, shown on the settings page. */
  description?: string;
  /** Permission to see the entries. Changes need `<module>.settings.manage`. */
  read: string;
  fields?: readonly CatalogFieldDeclaration[];
}

/** A catalog as the kernel serves it: declared by a module, derived from findings or the kernel's own. */
export interface ResolvedCatalog {
  key: string;
  /** Module ID, `core` for the kernel catalogs. */
  owner: string;
  label: string;
  description: string | null;
  /** Any of these permissions lets a person read the entries; `null` lets everyone of the company. */
  read: readonly string[] | null;
  /** Permission to change the entries. */
  manage: string;
  fields: readonly CatalogFieldDeclaration[];
}

/** One entry as the API returns it. */
export interface CatalogEntry {
  id: string;
  catalog: string;
  name: string;
  /** Short form, e.g. `BS` for a category. */
  code: string | null;
  color: ModuleAccent | null;
  description: string | null;
  sortOrder: number;
  /** At most one entry per catalog, preselected in forms. */
  isDefault: boolean;
  /** Inactive entries are no longer offered; values that use them stay readable. */
  active: boolean;
  data: CatalogEntryData;
  version: number;
  updatedAt: string;
}

export type CatalogEntryData = Record<string, string | number | boolean | null>;

/** Entries per catalog and company at most. The largest defect catalog of BrandDoku has 519. */
export const MAX_CATALOG_ENTRIES = 5000;
export const CATALOG_NAME_MAX = 300;
export const CATALOG_CODE_MAX = 20;
export const CATALOG_DESCRIPTION_MAX = 4000;
export const CATALOG_TEXT_MAX = 2000;
export const CATALOG_TEXTAREA_MAX = 20_000;
/** Key of a property: stable, used in `data`, imports and exports. */
export const CATALOG_FIELD_KEY = /^[a-z][a-zA-Z0-9]{0,39}$/;

/** Statuses of findings, firm-wide, with the property `closed` (counts as done). */
export const FINDING_STATUS_CATALOG = 'core.finding-statuses';
/** Priorities of findings, firm-wide, with `level` (1 is the most urgent) and `dueDays`. */
export const FINDING_PRIORITY_CATALOG = 'core.finding-priorities';

/**
 * Permission to maintain the kernel catalogs of findings, on the settings page of every module
 * with findings.
 */
export const FINDINGS_MANAGE_PERMISSION = 'company.findings.manage';

/**
 * Catalogs of the kernel. Shared by all modules with findings, so that "open findings of an
 * object" means the same across modules.
 */
export const KERNEL_CATALOGS: readonly ResolvedCatalog[] = [
  {
    key: FINDING_STATUS_CATALOG,
    owner: 'core',
    label: 'findings.catalogs.statuses.label',
    description: 'findings.catalogs.statuses.description',
    read: null,
    manage: FINDINGS_MANAGE_PERMISSION,
    fields: [{ key: 'closed', label: 'findings.catalogFields.closed', type: 'boolean' }],
  },
  {
    key: FINDING_PRIORITY_CATALOG,
    owner: 'core',
    label: 'findings.catalogs.priorities.label',
    description: 'findings.catalogs.priorities.description',
    read: null,
    manage: FINDINGS_MANAGE_PERMISSION,
    fields: [
      { key: 'level', label: 'findings.catalogFields.level', type: 'number', required: true },
      { key: 'dueDays', label: 'findings.catalogFields.dueDays', type: 'number' },
    ],
  },
];

/** Keys of the catalogs a module with findings gets. */
export function findingCatalogKeys(moduleId: string) {
  return {
    groups: `${moduleId}.finding-groups`,
    categories: `${moduleId}.finding-categories`,
    templates: `${moduleId}.finding-templates`,
  };
}

/** Names a module must not use for its own catalogs: the kernel derives them from `findings`. */
export const FINDING_CATALOG_NAMES = ['finding-groups', 'finding-categories', 'finding-templates'];

function findingCatalogs(manifest: ModuleManifest): ResolvedCatalog[] {
  const keys = findingCatalogKeys(manifest.id);
  const read = [...new Set((manifest.findings ?? []).map((f) => f.read))];
  const manage = `${manifest.id}.settings.manage`;
  const base = { owner: manifest.id, read, manage };
  return [
    {
      ...base,
      key: keys.groups,
      label: 'findings.catalogs.groups.label',
      description: 'findings.catalogs.groups.description',
      fields: [],
    },
    {
      ...base,
      key: keys.categories,
      label: 'findings.catalogs.categories.label',
      description: 'findings.catalogs.categories.description',
      fields: [
        {
          key: 'group',
          label: 'findings.catalogFields.group',
          type: 'catalog',
          catalog: keys.groups,
        },
      ],
    },
    {
      ...base,
      key: keys.templates,
      label: 'findings.catalogs.templates.label',
      description: 'findings.catalogs.templates.description',
      fields: [
        {
          key: 'category',
          label: 'findings.catalogFields.category',
          type: 'catalog',
          catalog: keys.categories,
          required: true,
        },
        {
          key: 'priority',
          label: 'findings.catalogFields.priority',
          type: 'catalog',
          catalog: FINDING_PRIORITY_CATALOG,
        },
        { key: 'description', label: 'findings.catalogFields.description', type: 'textarea' },
        { key: 'measure', label: 'findings.catalogFields.measure', type: 'textarea' },
        { key: 'legalBasis', label: 'findings.catalogFields.legalBasis', type: 'text' },
        { key: 'keywords', label: 'findings.catalogFields.keywords', type: 'text' },
      ],
    },
  ];
}

/**
 * All catalogs of the given modules: the kernel catalogs if a module declares findings, then per
 * module its declared catalogs and those derived from its findings.
 */
export function buildCatalogs(modules: readonly ModuleManifest[]): ResolvedCatalog[] {
  const findings = modules.some((m) => (m.findings ?? []).length > 0);
  return [
    ...(findings ? KERNEL_CATALOGS : []),
    ...modules.flatMap((m) => [
      ...(m.catalogs ?? []).map((c): ResolvedCatalog => ({
        key: c.key,
        owner: m.id,
        label: c.label,
        description: c.description ?? null,
        read: [c.read],
        manage: `${m.id}.settings.manage`,
        fields: c.fields ?? [],
      })),
      ...((m.findings ?? []).length > 0 ? findingCatalogs(m) : []),
    ]),
  ];
}

/** How `catalog` fields name the other entry: by ID in the API, by name in imports and defaults. */
export type CatalogReference = 'id' | 'name';

function fieldSchema(field: CatalogFieldDeclaration, references: CatalogReference) {
  switch (field.type) {
    case 'text':
      return z.string().trim().max(CATALOG_TEXT_MAX);
    case 'textarea':
      return z.string().trim().max(CATALOG_TEXTAREA_MAX);
    case 'number':
      return z.number().min(-1e9).max(1e9);
    case 'boolean':
      return z.boolean();
    case 'catalog':
      return references === 'id' ? z.uuid() : z.string().trim().min(1).max(CATALOG_NAME_MAX);
  }
}

/**
 * Checks the properties of an entry against the fields of its catalog: known keys, types,
 * lengths, required fields. Whether a `catalog` value names an entry of that catalog checks the
 * server. Empty texts count as missing.
 */
export function catalogDataSchema(
  fields: readonly CatalogFieldDeclaration[],
  references: CatalogReference = 'id',
) {
  const shape: Record<string, z.ZodType> = {};
  for (const field of fields) {
    const max = field.type === 'text' ? CATALOG_TEXT_MAX : CATALOG_TEXTAREA_MAX;
    if (!field.required) shape[field.key] = fieldSchema(field, references).nullable().optional();
    else if (field.type === 'text' || field.type === 'textarea') {
      shape[field.key] = z.string().trim().min(1).max(max);
    } else shape[field.key] = fieldSchema(field, references);
  }
  return z.strictObject(shape);
}

/** Standard fields of an entry as forms and the API take them. */
export const catalogEntryFieldsSchema = z.strictObject({
  name: z.string().trim().min(1).max(CATALOG_NAME_MAX),
  code: z.string().trim().max(CATALOG_CODE_MAX).nullable(),
  color: z.enum(MODULE_ACCENTS).nullable(),
  description: z.string().trim().max(CATALOG_DESCRIPTION_MAX).nullable(),
  isDefault: z.boolean(),
  active: z.boolean(),
});

/**
 * An entry to load into a catalog, from the defaults of a module or from a file. `catalog`
 * fields hold the name of the entry in the other catalog; the kernel resolves it when loading.
 * Loading matches entries by name (ignoring case) and leaves existing ones as they are.
 */
export interface CatalogImportEntry {
  name: string;
  code?: string | null;
  color?: ModuleAccent | null;
  description?: string | null;
  isDefault?: boolean;
  active?: boolean;
  data?: CatalogEntryData;
}

/** Standard fields of an import entry; `data` is checked with `catalogDataSchema(fields, 'name')`. */
export const catalogImportEntrySchema = z.strictObject({
  name: z.string().trim().min(1).max(CATALOG_NAME_MAX),
  code: z.string().trim().max(CATALOG_CODE_MAX).nullable().optional(),
  color: z.enum(MODULE_ACCENTS).nullable().optional(),
  description: z.string().trim().max(CATALOG_DESCRIPTION_MAX).nullable().optional(),
  isDefault: z.boolean().optional(),
  active: z.boolean().optional(),
  data: z
    .record(
      z.string().regex(CATALOG_FIELD_KEY),
      z.union([z.string().max(CATALOG_TEXTAREA_MAX), z.number(), z.boolean(), z.null()]),
    )
    .optional(),
});

/**
 * Entries of the kernel catalogs a company can load, in the language of the person loading them.
 * Taken from BrandDoku.
 */
export const KERNEL_CATALOG_DEFAULTS: Readonly<
  Record<'de' | 'en', Readonly<Record<string, readonly CatalogImportEntry[]>>>
> = {
  de: {
    [FINDING_STATUS_CATALOG]: [
      { name: 'Offen', code: 'OFF', color: 'red', isDefault: true, data: { closed: false } },
      { name: 'In Bearbeitung', code: 'INB', color: 'amber', data: { closed: false } },
      { name: 'Behoben', code: 'BEH', color: 'green', data: { closed: true } },
      { name: 'Nicht relevant', code: 'NRL', color: 'slate', data: { closed: true } },
    ],
    [FINDING_PRIORITY_CATALOG]: [
      { name: 'Sofortmaßnahme', color: 'red', data: { level: 1, dueDays: 1 } },
      { name: 'Hoch', color: 'orange', data: { level: 2, dueDays: 14 } },
      { name: 'Mittel', color: 'amber', isDefault: true, data: { level: 3, dueDays: 60 } },
      { name: 'Gering', color: 'green', data: { level: 4, dueDays: 180 } },
    ],
  },
  en: {
    [FINDING_STATUS_CATALOG]: [
      { name: 'Open', code: 'OPN', color: 'red', isDefault: true, data: { closed: false } },
      { name: 'In progress', code: 'INP', color: 'amber', data: { closed: false } },
      { name: 'Resolved', code: 'RES', color: 'green', data: { closed: true } },
      { name: 'Not relevant', code: 'NRL', color: 'slate', data: { closed: true } },
    ],
    [FINDING_PRIORITY_CATALOG]: [
      { name: 'Immediate action', color: 'red', data: { level: 1, dueDays: 1 } },
      { name: 'High', color: 'orange', data: { level: 2, dueDays: 14 } },
      { name: 'Medium', color: 'amber', isDefault: true, data: { level: 3, dueDays: 60 } },
      { name: 'Low', color: 'green', data: { level: 4, dueDays: 180 } },
    ],
  },
};
